import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { OverviewPage } from './pages/OverviewPage';
import { LiveScansPage } from './pages/LiveScansPage';
import { HistoryPage } from './pages/HistoryPage';
import { GeneratePage } from './pages/GeneratePage';
import { SettingsPage } from './pages/SettingsPage';
import { StatsPage } from './pages/StatsPage';
import { LoginPage } from './pages/LoginPage';
import { useAuth } from './hooks/useAuth';
import { isAdsEnabled, isInteriorEnabled, isVideoEnabled } from './utils/api';
import { VideoLiveScansPage } from './video/pages/LiveScansPage';
import { VideoHistoryPage } from './video/pages/HistoryPage';
import { VideoSettingsPage } from './video/pages/SettingsPage';
import { VideoOverviewPage } from './video/pages/OverviewPage';
import { VideoStatsPage } from './video/pages/StatsPage';
import { InteriorOverviewPage } from './interior/pages/OverviewPage';
import { InteriorLiveScansPage } from './interior/pages/LiveScansPage';
import { InteriorHistoryPage } from './interior/pages/HistoryPage';
import { InteriorStatsPage } from './interior/pages/StatsPage';
import { InteriorSettingsPage } from './interior/pages/SettingsPage';
import { InteriorGeneratePage } from './interior/pages/GeneratePage';
import { ManualVerificationPage } from './interior/pages/ManualVerificationPage';
import { ReviewScanPage } from './interior/pages/ReviewScanPage';
import { AdsOverviewPage } from './ads/pages/OverviewPage';
import { AdsLiveScansPage } from './ads/pages/LiveScansPage';
import { AdsHistoryPage } from './ads/pages/HistoryPage';
import { AdsStatsPage } from './ads/pages/StatsPage';
import { AdsSettingsPage } from './ads/pages/SettingsPage';
import { AdsGeneratePage } from './ads/pages/GeneratePage';

/**
 * Tiny helper component used by the legacy redirects. `useLocation` lets
 * us preserve the query string when bouncing `/foo?bar=1` to
 * `/lightning/foo?bar=1`, which matters for any external bookmark / link
 * still pointing at the pre-split URLs.
 */
function LegacyRedirect({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

/**
 * Top-level routing for the unified SPA.
 *
 * The shell hosts three mini-apps that are always deployed side-by-side:
 *   - `/lightning/*`  — the original lightning UI (default app)
 *   - `/video/*`      — the 360-video UI
 *   - `/interior/*`   — the 360-interior UI (apollo cropper + enhancer)
 *
 * The `videoEnabled` / `interiorEnabled` runtime checks below are
 * dev-mode safety nets for developers running `make web-dev` without
 * setting `VITE_VIDEO_API_BASE` / `VITE_INTERIOR_API_BASE` in their
 * `.env` — in that case the corresponding routes stay unmounted to
 * avoid surfacing a tab that would just 502. Every official build (CI
 * + `make deploy`) sets the env vars, so the routes are always present
 * in real deployments.
 *
 * Every old top-level path (`/overview`, `/live`, `/history`, …) is
 * preserved as a permanent redirect to its `/lightning/*` equivalent so
 * existing bookmarks, scripts, and external integrations don't break.
 */
function App() {
  const { isAuthenticated } = useAuth();
  const videoEnabled = isVideoEnabled();
  const interiorEnabled = isInteriorEnabled();
  const adsEnabled = isAdsEnabled();
  // Manual review is now a per-request flag (settings.manual_review), so
  // any interior job may be held for review regardless of deployment
  // config. The Manual Review screens are therefore always available.
  const manualReviewEnabled = true;

  return (
    <BrowserRouter>
      {!isAuthenticated ? (
        <LoginPage />
      ) : (
        <AppShell>
          <Routes>
            {/* ---- Lightning routes (default app) ---- */}
            <Route path="/lightning/overview" element={<OverviewPage />} />
            <Route path="/lightning/live" element={<LiveScansPage />} />
            <Route path="/lightning/history" element={<HistoryPage />} />
            <Route path="/lightning/generate" element={<GeneratePage />} />
            <Route path="/lightning/settings" element={<SettingsPage />} />
            <Route path="/lightning/stats" element={<StatsPage />} />
            <Route path="/lightning" element={<Navigate to="/lightning/overview" replace />} />
            <Route path="/lightning/monitor" element={<Navigate to="/lightning/live" replace />} />

            {/* ---- Video routes (always present in CI/`make deploy` builds) ---- */}
            {videoEnabled && (
              <>
                <Route path="/video/overview" element={<VideoOverviewPage />} />
                <Route path="/video/live" element={<VideoLiveScansPage refreshKey={0} />} />
                <Route path="/video/history" element={<VideoHistoryPage />} />
                <Route path="/video/stats" element={<VideoStatsPage />} />
                <Route path="/video/settings" element={<VideoSettingsPage />} />
                <Route path="/video" element={<Navigate to="/video/overview" replace />} />
              </>
            )}

            {/* ---- Interior routes (always present in CI/`make deploy` builds) ---- */}
            {interiorEnabled && (
              <>
                <Route path="/interior/overview" element={<InteriorOverviewPage />} />
                <Route path="/interior/live" element={<InteriorLiveScansPage />} />
                <Route path="/interior/history" element={<InteriorHistoryPage />} />
                <Route
                  path="/interior/manual-verification"
                  element={
                    manualReviewEnabled ? (
                      <ManualVerificationPage />
                    ) : (
                      <Navigate to="/interior/history" replace />
                    )
                  }
                />
                <Route
                  path="/interior/manual-verification/:jobId"
                  element={
                    manualReviewEnabled ? (
                      <ReviewScanPage />
                    ) : (
                      <Navigate to="/interior/history" replace />
                    )
                  }
                />
                <Route path="/interior/stats" element={<InteriorStatsPage />} />
                <Route path="/interior/settings" element={<InteriorSettingsPage />} />
                <Route path="/interior/generate" element={<InteriorGeneratePage />} />
                <Route path="/interior" element={<Navigate to="/interior/overview" replace />} />
              </>
            )}

            {/* ---- Ads routes (always present in CI/`make deploy` builds) ---- */}
            {adsEnabled && (
              <>
                <Route path="/ads/overview" element={<AdsOverviewPage />} />
                <Route path="/ads/live" element={<AdsLiveScansPage />} />
                <Route path="/ads/history" element={<AdsHistoryPage />} />
                <Route path="/ads/stats" element={<AdsStatsPage />} />
                <Route path="/ads/settings" element={<AdsSettingsPage />} />
                <Route path="/ads/generate" element={<AdsGeneratePage />} />
                <Route path="/ads" element={<Navigate to="/ads/overview" replace />} />
              </>
            )}

            {/* ---- Legacy redirects (preserve query/hash) ---- */}
            <Route path="/overview" element={<LegacyRedirect to="/lightning/overview" />} />
            <Route path="/live" element={<LegacyRedirect to="/lightning/live" />} />
            <Route path="/history" element={<LegacyRedirect to="/lightning/history" />} />
            <Route path="/generate" element={<LegacyRedirect to="/lightning/generate" />} />
            <Route path="/settings" element={<LegacyRedirect to="/lightning/settings" />} />
            <Route path="/stats" element={<LegacyRedirect to="/lightning/stats" />} />
            <Route path="/monitor" element={<LegacyRedirect to="/lightning/live" />} />

            {/* Default + 404: send everything to the lightning overview. */}
            <Route path="/" element={<Navigate to="/lightning/overview" replace />} />
            <Route path="*" element={<Navigate to="/lightning/overview" replace />} />
          </Routes>
        </AppShell>
      )}
    </BrowserRouter>
  );
}

export default App;
